import React, { createContext, useCallback, useEffect, useState } from 'react';

/**
 * Authentication Context
 * Manages user authentication state, tokens, and permissions
 * Provides auth data to all components
 */
export const AuthContext = createContext();
const API = process.env.REACT_APP_API_BASE_URL || `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth from localStorage on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedAccessToken = localStorage.getItem('accessToken');
        const storedRefreshToken = localStorage.getItem('refreshToken');
        const storedUser = localStorage.getItem('user');

        if (storedAccessToken && storedUser) {
          setAccessToken(storedAccessToken);
          setRefreshToken(storedRefreshToken);
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);

          // Verify token is still valid by fetching user profile
          try {
            const response = await fetch(`${API}/auth/me`, {
              headers: {
                Authorization: `Bearer ${storedAccessToken}`,
              },
            });

            if (response.ok) {
              const userData = await response.json();
              setUser(userData);
            } else if (response.status === 401 && storedRefreshToken) {
              // Token expired, try to refresh
              await refreshAccessToken(storedRefreshToken);
            } else {
              // Token invalid, logout
              logout();
            }
          } catch (err) {
            console.error('Error verifying token:', err);
            logout();
          }
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /**
   * Login user with credentials
   */
  const login = useCallback(async (username, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Login failed');
      }

      const data = await response.json();

      // Store tokens and user
      localStorage.setItem('accessToken', data.access_token);
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('refreshToken', data.refresh_token);
      localStorage.setItem('user', JSON.stringify(data.user));

      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      setUser(data.user);
      setIsAuthenticated(true);

      return { success: true, user: data.user };
    } catch (err) {
      const errorMsg = err.message || 'Login failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Register new user
   */
  const register = useCallback(async (username, email, fullName, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          email,
          full_name: fullName,
          password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Registration failed');
      }

      const data = await response.json();

      // Store tokens and user
      localStorage.setItem('accessToken', data.access_token);
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('refreshToken', data.refresh_token);
      localStorage.setItem('user', JSON.stringify(data.user));

      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      setUser(data.user);
      setIsAuthenticated(true);

      return { success: true, user: data.user };
    } catch (err) {
      const errorMsg = err.message || 'Registration failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Refresh access token using refresh token
   */
  const refreshAccessToken = useCallback(async (token) => {
    try {
      const response = await fetch(`${API}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: token,
        }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();

      localStorage.setItem('accessToken', data.access_token);
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('auth_token', data.access_token);
      setAccessToken(data.access_token);

      return true;
    } catch (err) {
      console.error('Error refreshing token:', err);
      logout();
      return false;
    }
  }, []);

  /**
   * Logout user and clear auth state
   */
  const logout = useCallback(async () => {
    try {
      // Optional: Notify backend of logout
      if (accessToken) {
        await fetch(`${API}/auth/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }).catch(() => {
          // Ignore errors during logout
        });
      }
    } finally {
      // Clear local state and storage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('token');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');

      setAccessToken(null);
      setRefreshToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
    }
  }, [accessToken]);

  /**
   * Check if user has specific permission
   */
  const hasPermission = useCallback((permission) => {
    if (!user || !user.role) return false;

    if (['Admin', 'SuperAdmin'].includes(user.role)) return true;
    return Array.isArray(user.permissions) && (user.permissions.includes(permission) || user.permissions.includes('*'));
  }, [user]);

  /**
   * Check if user has specific role
   */
  const hasRole = useCallback((role) => {
    if (!user) return false;
    return user.role === role || Array.isArray(role) && role.includes(user.role);
  }, [user]);

  /**
   * Change password
   */
  const changePassword = useCallback(async (currentPassword, newPassword, confirmPassword) => {
    setError(null);

    try {
      const response = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Password change failed');
      }

      return { success: true };
    } catch (err) {
      const errorMsg = err.message || 'Password change failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, [accessToken]);

  const value = {
    // State
    user,
    loading,
    error,
    isAuthenticated,
    accessToken,
    refreshToken,

    // Methods
    login,
    register,
    logout,
    hasPermission,
    hasRole,
    changePassword,
    refreshAccessToken,

    // Helpers
    setError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
